import { useState } from 'react'
import { X, Loader2, Check } from 'lucide-react'
import { calendarApi } from '../../services/calendarApi'

// OSPROD-MAIL-CAL-L3: two-step CalDAV connect flow. Step 1 — verify
// credentials and discover calendars. Step 2 — user picks which
// calendars to ingest. Submitted as PATCH /accounts/:id with the
// selected calendar uids.

interface Props {
  onClose: () => void
  onConnected: (accountId: number) => void
}

interface Step1Result {
  accountId: number
  calendars: Array<{ uid: string; name: string; color?: string }>
}

export default function CalDAVConnectModal({ onClose, onConnected }: Props) {
  const [creds, setCreds] = useState({ url: '', user: '', password: '' })
  const [step, setStep] = useState<'creds' | 'pick'>('creds')
  const [result, setResult] = useState<Step1Result | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submitCreds = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await calendarApi.caldavConnect(creds)
      setResult(res.data)
      // Default-select all calendars; user can deselect noisy ones.
      setSelected(new Set(res.data.calendars.map((c) => c.uid)))
      setStep('pick')
    } catch (err: any) {
      const code = err?.response?.data?.code
      const message = err?.response?.data?.error
      setError(
        code === 'AUTH_FAILED' ? 'Username or password is incorrect.' :
        code === 'PROPFIND_FAILED' ? 'The server did not respond like a CalDAV endpoint.' :
        message || 'Failed to connect.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const submitPick = async () => {
    if (!result) return
    setSubmitting(true)
    try {
      await calendarApi.setActiveCalendars(result.accountId, Array.from(selected))
      onConnected(result.accountId)
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save calendar selection.')
    } finally {
      setSubmitting(false)
    }
  }

  const toggle = (uid: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-xl shadow-2xl w-[460px] max-w-[95vw]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-foreground font-semibold">
            {step === 'creds' ? 'Add CalDAV calendar' : 'Pick calendars to import'}
          </h3>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        {step === 'creds' && (
          <form onSubmit={submitCreds}>
            <div className="p-5 space-y-3">
              <Field label="CalDAV URL">
                <input
                  required value={creds.url}
                  onChange={(e) => setCreds({ ...creds, url: e.target.value })}
                  placeholder="https://caldav.icloud.com/"
                  className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
                />
              </Field>
              <Field label="Username">
                <input
                  required value={creds.user}
                  onChange={(e) => setCreds({ ...creds, user: e.target.value })}
                  className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
                />
              </Field>
              <Field label="Password (app-specific for iCloud)">
                <input
                  type="password" required value={creds.password}
                  onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                  className="w-full bg-muted rounded px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
                />
              </Field>
              {error && <div className="text-xs text-red-400 bg-red-500/10 rounded px-3 py-2">{error}</div>}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
              <button type="button" onClick={onClose} disabled={submitting}
                className="px-4 py-2 text-sm rounded-md text-muted-foreground hover:text-foreground transition disabled:opacity-50">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition">
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? 'Connecting…' : 'Continue'}
              </button>
            </div>
          </form>
        )}

        {step === 'pick' && result && (
          <>
            <div className="p-5 space-y-2 max-h-[50vh] overflow-y-auto">
              <p className="text-xs text-muted-foreground mb-2">
                Found {result.calendars.length} calendar{result.calendars.length === 1 ? '' : 's'}. Pick which ones to sync — you can change this later.
              </p>
              {result.calendars.map((c) => {
                const checked = selected.has(c.uid)
                return (
                  <button
                    key={c.uid}
                    type="button"
                    onClick={() => toggle(c.uid)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded transition ${
                      checked ? 'bg-primary/10' : 'hover:bg-muted/60'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded border flex items-center justify-center ${
                      checked ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground'
                    }`}>
                      {checked && <Check size={12} />}
                    </span>
                    {c.color && (
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                    )}
                    <span className="text-sm text-foreground">{c.name}</span>
                  </button>
                )
              })}
              {error && <div className="text-xs text-red-400 bg-red-500/10 rounded px-3 py-2 mt-2">{error}</div>}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
              <button type="button" onClick={() => setStep('creds')} disabled={submitting}
                className="px-4 py-2 text-sm rounded-md text-muted-foreground hover:text-foreground transition disabled:opacity-50">
                Back
              </button>
              <button onClick={submitPick} disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition">
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {submitting ? 'Saving…' : `Save (${selected.size})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  )
}
